import type { ProgressResponse } from "./progressTypes";
import { useCurrentRepo } from "./router";
import { LabelledBarChart } from "./LabelledBarChart";
import { DailyBarChart } from "./DailyBarChart";
import { useApiResource } from "./useApiResource";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

function formatMergeHours(hours: number): string {
  if (hours < 48) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

// User-requested: while this page is open, it should pick up new GitHub
// activity rather than only ever showing what was true at page load.
// Matches SYNC_GITHUB_INTERVAL's own default (5 minutes) — the backend's
// per-section cache TTL was shortened to match, so a refresh at this
// cadence actually has a chance of seeing new data, not just re-reading the
// same cached response. Only fires while the tab is visible (see
// useApiResource's own doc comment) — a backgrounded tab generates no
// extra GitHub-backed API calls.
const PROGRESS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// ProgressPage combines programme-wide PR activity and per-repo daily graphs
// with the default repository's ADR velocity and recent documentation changes.
// Each I/O-backed section renders its own availability; partial PR results
// remain visible with explicit warnings rather than hiding healthy repos.
export function ProgressPage({ token }: { token: string }) {
  const repo = useCurrentRepo();
  const { status, data, message, updatedAt } = useApiResource<ProgressResponse>(token, repo, "/progress", { refreshIntervalMs: PROGRESS_REFRESH_INTERVAL_MS });

  if (status === "loading") {
    return (
      <section className="panel">
        <h2>Progress</h2>
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready") {
    return (
      <section className="panel">
        <h2>Progress</h2>
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }
  if (!data) return null; // unreachable once status === "ready"

  const entries = data.changelog.entries ?? [];
  const byMonth = data.adrVelocity.byMonth ?? [];
  const mergedByMonth = data.pullRequests.mergedByMonth ?? [];
  const recent = data.pullRequests.recent ?? [];
  const prWarnings = data.pullRequests.warnings ?? [];
  const commitActivity = data.commitActivity ?? [];

  return (
    <>
      {updatedAt && <p className="sync-note">Last updated {updatedAt.toLocaleTimeString()} — refreshes automatically every 5 minutes while this tab is open and visible.</p>}
      <section className="panel">
        <h2>Commit activity</h2>
        <p className="muted-note">
          Daily commits, merged pull requests and closed issues across each repo, over the last 30 days.
        </p>
        {commitActivity.length === 0 ? (
          <p className="muted-note">No repos configured.</p>
        ) : (
          commitActivity.map((ra) => (
            <div key={ra.repoKey} className="commit-activity-repo">
              <h3>{ra.displayName}</h3>
              {!ra.commits.available && <p className="err">Commits: {ra.commits.error}</p>}
              {!ra.merges.available && <p className="err">Merges: {ra.merges.error}</p>}
              {!ra.issues.available && <p className="err">Issues closed: {ra.issues.error}</p>}
              {(ra.commits.available || ra.merges.available || ra.issues.available) && (
                <DailyBarChart
                  commits={ra.commits.byDay ?? []}
                  merges={ra.merges.byDay ?? []}
                  issues={ra.issues.byDay ?? []}
                />
              )}
            </div>
          ))
        )}
      </section>

      <section className="panel">
        <h2>ADR velocity</h2>
        <dl className="stat-grid">
          <div>
            <dt>Total ADRs</dt>
            <dd>{data.adrVelocity.totalAdrs}</dd>
          </div>
          <div>
            <dt>With measured turnaround</dt>
            <dd>{data.adrVelocity.withMeasuredTurnaround}</dd>
          </div>
          <div title="Days between an ADR's proposed and accepted dates, for ADRs where both are recorded.">
            <dt>Median proposed → accepted</dt>
            <dd>{data.adrVelocity.withMeasuredTurnaround > 0 ? `${data.adrVelocity.medianTurnaroundDays.toFixed(1)}d` : "—"}</dd>
          </div>
        </dl>
        <h3>By month</h3>
        <p className="muted-note">Each ADR counted once, in the month of its latest recorded date.</p>
        {byMonth.length === 0 ? <p className="muted-note">No dated ADR activity recorded.</p> : <LabelledBarChart data={byMonth.map((m) => ({ label: m.month, count: m.count }))} ariaLabel="ADRs by month" />}
      </section>

      <section className="panel">
        <h2>Pull request activity</h2>
        <p className="muted-note">Across all configured repositories. Statistics cover PRs opened in the last 180 days.</p>
        {prWarnings.length > 0 && (
          <div role="status" className="err">
            <p>{data.pullRequests.available ? "Partial data — some repository activity may be missing." : "Repository data unavailable."}</p>
            <ul>{prWarnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
          </div>
        )}
        {!data.pullRequests.available ? (
          <p className="err">{data.pullRequests.error}</p>
        ) : (
          <>
            <dl className="stat-grid">
              <div>
                <dt>Open</dt>
                <dd>{data.pullRequests.openCount}</dd>
              </div>
              <div>
                <dt>Merged, last 24 hours</dt>
                <dd>{data.pullRequests.mergedLast24Hours}</dd>
              </div>
              <div>
                <dt>Merged, last 7 days</dt>
                <dd>{data.pullRequests.mergedLast7Days}</dd>
              </div>
              <div>
                <dt>Merged, last 30 days</dt>
                <dd>{data.pullRequests.mergedLast30Days}</dd>
              </div>
              <div>
                <dt>Merged, last 90 days</dt>
                <dd>{data.pullRequests.mergedLast90Days}</dd>
              </div>
              <div>
                <dt>Median time to merge</dt>
                <dd>{formatMergeHours(data.pullRequests.medianMergeHours)}</dd>
              </div>
            </dl>
            <h3>Merged by month</h3>
            {mergedByMonth.length === 0 ? (
              <p className="muted-note">No merged pull requests in the fetched window.</p>
            ) : (
              <LabelledBarChart data={mergedByMonth.map((m) => ({ label: m.month, count: m.count }))} ariaLabel="Merged pull requests by month" />
            )}
            <h3>Recent pull requests</h3>
            <p className="muted-note">The newest 20 PRs across configured repositories, ordered by creation time within the same 180-day window.</p>
            {recent.length === 0 ? (
              <p className="muted-note">No recent pull requests.</p>
            ) : (
              <ul className="priority-list">
                {recent.map((pr) => (
                  <li key={`${pr.repository ?? pr.url}:${pr.number}`}>
                    <div className="priority-head">
                      <span className="priority-name">
                        <a href={pr.url} target="_blank" rel="noopener noreferrer">
                          {pr.repository ? `${pr.repository} ` : ""}#{pr.number} {pr.title}
                        </a>
                      </span>
                      <span className={`pr-state pr-state-${pr.state}`}>{pr.state}</span>
                    </div>
                    <p className="priority-reasons">
                      {pr.author} · opened {formatDate(pr.createdAt)}
                      {pr.mergedAt ? ` · merged ${formatDate(pr.mergedAt)}` : ""}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      <section className="panel">
        <h2>Recent changes</h2>
        {!data.changelog.available ? (
          <p className="err">{data.changelog.error}</p>
        ) : entries.length === 0 ? (
          <p className="muted-note">No recent commits.</p>
        ) : (
          <ul className="priority-list">
            {entries.map((e) => (
              <li key={e.sha}>
                <div className="priority-head">
                  <span className="priority-name">{e.message.split("\n")[0]}</span>
                  <span className="muted-note">{formatDate(e.date)}</span>
                </div>
                <p className="priority-reasons">
                  {e.author} ·{" "}
                  <a className="mono" href={e.url} target="_blank" rel="noopener noreferrer">
                    {e.sha.slice(0, 7)}
                  </a>
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
